import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickableText } from 'ClickableText';
import { ShowMoreCollapse } from './ShowMoreCollapse';
import { BandSectionHeading } from './BandSectionHeading';
import { type ExternalLinkMetadatum, type LessonForStreamDashboard, type StreamForStreamDashboard } from 'Lessons';
import { getChapterLessons, getChapterLessonsIndex, getExternalLinkTitle } from '../selectors';
import '../locales/streamDashboard/streamDashboard-en.json';

// Assigned Reading, grouped by lesson. No horizontal padding: the parent .dashboard-container supplies it.

type LinkGroupProps = {
    stream: StreamForStreamDashboard;
    chapterIndex: number;
    lesson: LessonForStreamDashboard;
    links: ExternalLinkMetadatum[];
    onOpenLink: (url: string) => void;
};

const LessonLinkGroup: FC<LinkGroupProps> = ({ stream, chapterIndex, lesson, links, onOpenLink }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });
    const lessonNumber = (getChapterLessonsIndex(stream, lesson) ?? 0) + 1;

    return (
        <div className="mb-9 last:mb-0">
            {/* .lesson-index-box is a block, so the numbers take their own line and the title follows. */}
            <div className="mb-5 ms-1">
                <div className="text-xs font-medium text-slate-grey-500">
                    <span>{t('chapter_number_x', { number: chapterIndex + 1 })}</span>
                    <span className="px-[3px]" aria-hidden>
                        |
                    </span>
                    <span>{t('lesson_number_x', { number: lessonNumber })}</span>
                </div>
                <div className="text-lg font-medium text-slate-grey">{lesson.title.toUpperCase()}</div>
            </div>

            {links.map(link => (
                <div key={link.id} className="mb-1 rounded-2 p-1.5 hover:bg-slate-grey-50">
                    <div>
                        <ClickableText
                            onPress={() => onOpenLink(link.url)}
                            color="primary"
                            underline="none"
                            weight="semibold"
                            allowWrap
                            className="text-start text-lg"
                        >
                            {getExternalLinkTitle(link) ?? t('title_not_available')}
                        </ClickableText>
                        {!link.required && (
                            <span className="ms-2 text-3xs font-semibold uppercase text-primary">{t('optional')}</span>
                        )}
                    </div>
                    {/* Main made the url a second link to the same target, so keep it clickable. */}
                    <ClickableText
                        onPress={() => onOpenLink(link.url)}
                        color="slate-grey"
                        underline="none"
                        allowWrap
                        className="text-start leading-4.5"
                    >
                        <span className="text-2xs text-slate-grey-500">{link.url}</span>
                    </ClickableText>
                </div>
            ))}
        </div>
    );
};

type Props = {
    stream: StreamForStreamDashboard;
    linksByLessonId: Record<string, ExternalLinkMetadatum[]>;
    isCollapsed: boolean;
    onShowAll: () => void;
    onOpenLink: (url: string) => void;
    brandName: string;
};

export const ExternalLinksSection: FC<Props> = ({
    stream,
    linksByLessonId,
    isCollapsed,
    onShowAll,
    onOpenLink,
    brandName,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <section className="pt-12.5">
            {/* Padding, not margin, so the gap stays outside the collapsed area. */}
            <BandSectionHeading
                caption={t('external_links')}
                intro={t('external_links_intro', { brandName })}
                introClassName="pb-3.5 sm:pb-6.25"
            />

            {/* Cut short on mobile until the student asks for the rest. */}
            <ShowMoreCollapse collapsed={isCollapsed} onShowMore={onShowAll}>
                {stream.chapters.map((chapter, chapterIndex) =>
                    getChapterLessons(stream, chapter).map(lesson => {
                        const links = linksByLessonId[lesson.id];
                        if (!links?.length) return null;

                        return (
                            <LessonLinkGroup
                                key={lesson.id}
                                stream={stream}
                                chapterIndex={chapterIndex}
                                lesson={lesson}
                                links={links}
                                onOpenLink={onOpenLink}
                            />
                        );
                    }),
                )}
            </ShowMoreCollapse>
        </section>
    );
};
